import React, { useState } from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import Button from '@amzn/meridian/button';
import { colorBlue600 } from '@amzn/meridian-tokens/base/color';
import { saveEosWashReport } from '../api';
import { EosWashReport } from '../model';
import { formatTimestamp } from '../utils';
import { getUserName } from '../../../../services/boson/bosonClient';
import { useAppDispatch } from '../../../../store';
import { showError } from '../../../../store/slices/errorSlice';

/**
 * Editable in-shift EOS (end-of-shift) wash report card: a dark header bar over a free-text
 * editor, with a Save button in the bottom-right corner. Saving creates a new immutable version
 * (optimistic-locked on baseVersion) via SaveEosWashReport, then reflects the new version and
 * attribution line. A stale-version conflict surfaces as an error asking the user to refresh.
 */
export interface InShiftEosWashProps {
  /** Shift plan the report is attached to (SaveEosWashReport partition key). Save is disabled when absent. */
  baselineShiftPlanId?: string;
  /** Latest report from the compare response, used to seed the editor and version. */
  report?: EosWashReport;
  testId?: string;
}

const buildByline = (createdBy?: string, createdAt?: number): string | undefined => {
  const parts: string[] = [];
  if (createdBy) {
    parts.push(`Last updated by ${createdBy}`);
  }
  if (typeof createdAt === 'number') {
    parts.push(formatTimestamp(createdAt));
  }
  return parts.length > 0 ? parts.join(' • ') : undefined;
};

const InShiftEosWash: React.FC<InShiftEosWashProps> = ({ baselineShiftPlanId, report, testId }) => {
  const dispatch = useAppDispatch();
  const [text, setText] = useState<string>(report?.content ?? '');
  // The version the current text builds on; sent as baseVersion and bumped on each save.
  const [baseVersion, setBaseVersion] = useState<number>(report?.version ?? 0);
  const [byline, setByline] = useState<string | undefined>(
    buildByline(report?.createdBy, report?.createdAt),
  );
  const [saving, setSaving] = useState(false);

  const canSave = !!baselineShiftPlanId && text.trim().length > 0 && !saving;

  const handleSave = async () => {
    // Save is only invocable when canSave is true (button disabled otherwise), which
    // guarantees a non-empty baselineShiftPlanId — assert it here for the request type.
    setSaving(true);
    try {
      const createdBy = await getUserName();
      const result = await saveEosWashReport({
        baselineShiftPlanId: baselineShiftPlanId as string,
        content: text,
        createdBy,
        baseVersion,
      });
      setBaseVersion(result.version);
      setByline(buildByline(createdBy, result.createdAt));
    } catch (err: any) {
      dispatch(showError({
        title: 'Failed to save EOS Wash',
        message: err?.message
          || 'Could not save the EOS wash report. It may have been updated by someone else — refresh and try again.',
      }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box type="outline" spacingInset="none" data-testid={testId}>
      <Box backgroundColor={colorBlue600} spacingInset="300 400">
        <Text type="h100" color="inverted">
          EOS Wash
        </Text>
      </Box>
      <Box spacingInset="400">
        <Column spacing="300">
          <Textarea
            value={text}
            onChange={setText}
            placeholder="Add an end-of-shift wash note…"
            rows={4}
            disabled={saving}
            data-testid={testId ? `${testId}-input` : undefined}
          />
          <Row alignmentHorizontal="justify" alignmentVertical="center" width="100%">
            {byline ? (
              <Text type="b100" color="secondary" data-testid={testId ? `${testId}-byline` : undefined}>
                {byline}
              </Text>
            ) : (
              <span />
            )}
            <Button
              type="primary"
              size="small"
              onClick={handleSave}
              disabled={!canSave}
              data-testid={testId ? `${testId}-save` : undefined}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Row>
        </Column>
      </Box>
    </Box>
  );
};

export default InShiftEosWash;
